/**
 * Signature editing shared by the webmail settings and the admin console (one implementation, DESIGN.md §1.4):
 * name field, the one RichTextEditor, an optional "Insert placeholder" menu for directory values (central
 * templates), and a sandboxed preview of rendered signature HTML.
 *
 * The editor is uncontrolled: give the component a new `key` to load another signature.
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { BracesIcon } from 'lucide-react';
import { useId, useRef } from 'react';
import { Button } from '../components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/dropdown-menu';
import { Input } from '../components/input';
import { Label } from '../components/label';
import { LazyRichTextEditor } from '../editor/lazy-rich-text-editor';
import { sanitizeChange } from '../editor/source';
import type { RichTextEditorHandle, RichTextValue } from '../editor/rich-text-editor';
import { MailHtmlViewer } from '../mail/mail-panes';
import { mailLayoutHtml } from '../mail/outgoing';
import { plainTextToMailHtml } from '../mail/sanitize';
import { cn } from '../lib/utils';

/** Directory placeholders of central templates (same list as the platform API signatures module). */
export const SIGNATURE_PLACEHOLDERS = [
  'displayName',
  'givenName',
  'surname',
  'title',
  'department',
  'company',
  'email',
  'phone',
  'mobile',
  'office',
  'street',
  'city',
  'postalCode',
  'country',
] as const;
export type SignaturePlaceholder = (typeof SIGNATURE_PLACEHOLDERS)[number];

const PLACEHOLDER_LABELS: Record<SignaturePlaceholder, MessageKey> = {
  displayName: 'signatures.placeholder.displayName',
  givenName: 'signatures.placeholder.givenName',
  surname: 'signatures.placeholder.surname',
  title: 'signatures.placeholder.title',
  department: 'signatures.placeholder.department',
  company: 'signatures.placeholder.company',
  email: 'signatures.placeholder.email',
  phone: 'signatures.placeholder.phone',
  mobile: 'signatures.placeholder.mobile',
  office: 'signatures.placeholder.office',
  street: 'signatures.placeholder.street',
  city: 'signatures.placeholder.city',
  postalCode: 'signatures.placeholder.postalCode',
  country: 'signatures.placeholder.country',
};

/** All three input modes: signatures may need company-specific HTML. */
const SIGNATURE_MODES = ['formatted', 'source', 'plain'] as const;

/** Notice (message key) for what the server changed in saved HTML; null if it kept the HTML as sent. */
export function sanitizeNoticeKey(sent: string, saved: string): MessageKey | null {
  const change = sanitizeChange(sent, saved);
  return change === 'scripts' ? 'signatures.sanitizedScripts' : change === 'other' ? 'signatures.sanitizedOther' : null;
}

/** Editor value as stored signature HTML (plain text becomes simple HTML). */
const toHtml = (value: RichTextValue): string => (value.format === 'html' ? value.content : plainTextToMailHtml(value.content));

export interface SignatureEditorProps {
  name: string;
  onNameChange: (name: string) => void;
  /** Initial HTML (with placeholders for templates). */
  defaultHtml: string;
  onHtmlChange: (html: string) => void;
  /** Show the "Insert placeholder" menu. */
  placeholders?: boolean;
  /** Name validation message, shown under the field. */
  nameError?: string | undefined;
  className?: string;
}

export function SignatureEditor({ name, onNameChange, defaultHtml, onHtmlChange, placeholders = false, nameError, className }: SignatureEditorProps) {
  const { t } = useI18n();
  const nameId = useId();
  const editor = useRef<RichTextEditorHandle>(null);
  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={nameId}>{t('signatures.name')}</Label>
        <Input id={nameId} value={name} maxLength={100} aria-invalid={nameError !== undefined} onChange={(event) => onNameChange(event.target.value)} />
        {nameError && <p className="text-sm text-destructive">{nameError}</p>}
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label>{t('signatures.content')}</Label>
          {placeholders && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <BracesIcon />
                  {t('signatures.insertPlaceholder')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 w-64 overflow-y-auto">
                {SIGNATURE_PLACEHOLDERS.map((placeholder) => (
                  <DropdownMenuItem key={placeholder} onSelect={() => editor.current?.insertText(`{${placeholder}}`)}>
                    <span className="min-w-0 flex-1 truncate">{t(PLACEHOLDER_LABELS[placeholder])}</span>
                    <code className="text-xs text-muted-foreground">{`{${placeholder}}`}</code>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <LazyRichTextEditor
          handleRef={editor}
          label={t('signatures.content')}
          defaultValue={{ format: 'html', content: defaultHtml }}
          modes={SIGNATURE_MODES}
          onChange={(value) => onHtmlChange(toHtml(value))}
          className="h-64"
        />
      </div>
    </div>
  );
}

export interface SignaturePreviewProps {
  /** Rendered, sanitized HTML. */
  html: string;
  className?: string;
}

/** Rendered signature in the sandboxed mail viewer. */
export function SignaturePreview({ html, className }: SignaturePreviewProps) {
  const { t } = useI18n();
  return (
    <div className={cn('flex h-40 flex-col overflow-hidden rounded-md border bg-background', className)}>
      <MailHtmlViewer html={mailLayoutHtml(html)} allowRemote={false} title={t('signatures.preview')} />
    </div>
  );
}
